import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, Observer } from 'rxjs';
import { AuthService } from './auth.service';
import { EnvironmentService } from './environment.service';
import { PGUtilities } from '../utilities/pg-utilities';

class ApiInterceptorQueueItem { 
    req: HttpRequest<any>;
    next: HttpHandler;
    observer: Observer<HttpEvent<any>>;
}

type ApiInterceptorQueueKey = 'travel' | 'entourance';

class ApiInterceptorQueueState {
    items: Array<ApiInterceptorQueueItem> = [];
    lock = false;
    retryCount = 0;
}

/**
 * Auth model for this app:
 * - Login/session token comes from TravelAPIUrl (dev.travel)
 * - EntouranceAPIUrl is public-only (no Bearer from this app)
 *
 * Travel and Entourance use separate request queues so one API does not block the other.
 */
@Injectable({
    providedIn: 'root'
})
export class ApiInterceptor implements HttpInterceptor {

    constructor(private environmentService:EnvironmentService, private authService:AuthService) { }

    private _queues: { [key in ApiInterceptorQueueKey]: ApiInterceptorQueueState } = {
        travel: new ApiInterceptorQueueState(),
        entourance: new ApiInterceptorQueueState(),
    }

    private _isReleaseRequest(req: HttpRequest<any>) {
        return /^assets\/release\.json$/.test(req.url)
    }

    private _isAssetsRequest(req: HttpRequest<any>) {
        return /^assets\/[^\?]*$/.test(req.url)
    }

    private _isTravelAPIRequest(req: HttpRequest<any>) {
        const url = this.environmentService.environment.TravelAPIUrl;
        return !!url && req.url.startsWith(url);
    }

    private _isEntouranceAPIRequest(req: HttpRequest<any>) {
        const url = this.environmentService.environment.EntouranceAPIUrl;
        return !!url && req.url.startsWith(url);
    }

    private _queueKeyForRequest(req: HttpRequest<any>): ApiInterceptorQueueKey {
        if(this._isTravelAPIRequest(req)) return 'travel';
        if(this._isEntouranceAPIRequest(req)) return 'entourance';
        return null;
    }

    private _isTravelUnauthenticatedRequest(req: HttpRequest<any>) {
        if(!this._isTravelAPIRequest(req)) return false;
        const path = req.url.replace(this.environmentService.environment.TravelAPIUrl, '');
        return path.startsWith('/login') || path.startsWith('/otp');
    }

    /** Bearer only for Travel (non login/otp). Entourance stays public-only in this app. */
    private _requiresAuthToken(req: HttpRequest<any>) {
        return this._isTravelAPIRequest(req) && !this._isTravelUnauthenticatedRequest(req);
    }

    private async _checkAPIRequestAuthToken(req: HttpRequest<any>) {
        if(!this._requiresAuthToken(req)) {
            return req;
        }

        const token = await this.authService.getAuthToken(true);
        if(token) {
            return req.clone({
                setHeaders: {
                    'Authorization': 'Bearer ' + token,
                    'Accept': 'application/json',
                }
            });
        }
        return req;
    }

    private _captureEntouranceApiVersion(queueKey: ApiInterceptorQueueKey, resp: { headers?: { get(name: string): string | null } }) {
        if(queueKey != 'entourance' || resp?.headers == null) return;
        const version = resp.headers.get('api-version');
        if(version) {
            this.environmentService.apiVersion = version;
        }
    }

    private _calculateAPIRequestNextDelay(req:HttpRequest<any>, resp:HttpResponse<any>, time:number) {
        let _rateLimit = parseInt(resp.headers.get('x-ratelimit-limit'));
        let _rateRemaining = parseInt(resp.headers.get('x-ratelimit-remaining'));

        if(!isNaN(_rateLimit) && !isNaN(_rateRemaining)) {
            let _rateAverage = 60 / _rateLimit * 1000;
            let _ratePoint = 1 - (_rateRemaining / _rateLimit);

            let _nextDelay = Math.floor(Math.pow(_ratePoint, 2) * 4 * _rateAverage);
            let _cleanDelay = Math.max(1, _nextDelay - time);

            return _cleanDelay;
        }
        else {
            // Fallback pacing when rate-limit headers are missing (typical for Travel)
            let _nextDelay = 250;
            let _cleanDelay = Math.max(1, _nextDelay - time);

            return _cleanDelay;
        }
    }

    private _checkAPIRequestQueue(queueKey: ApiInterceptorQueueKey) {
        const queue = this._queues[queueKey];
        if(queue.lock) return;

        let _ctx = queue.items[0];
        if(_ctx == null) return;

        queue.lock = true;

        let _timeStart = Date.now();

        this._checkAPIRequestAuthToken(_ctx.req).then((req) => {
            _ctx.req = req;

            _ctx.next.handle(_ctx.req).subscribe((resp) => {
                if(resp.type == HttpEventType.Response) {
                    this._captureEntouranceApiVersion(queueKey, resp);
                    let _delay = this._calculateAPIRequestNextDelay(_ctx.req, resp, Date.now() - _timeStart);
                    this._onAPIRequestComplete(queueKey, this._onRequestSuccess(queueKey, _ctx, resp), _delay);
                }
                else {
                    _ctx.observer.next(resp);
                }
            }, (error) => {
                this._captureEntouranceApiVersion(queueKey, error);
                if(error.status == 401 && queueKey == 'travel' && this.authService.hasAuthToken()) {
                    setTimeout(() => {
                        this.authService.tryRefreshToken().then(() => {
                            queue.lock = false;
                            // After travel 401, drop the queued call — session is cleared
                            queue.items.shift();
                            this._checkAPIRequestQueue(queueKey);
                        }, () => {
                            queue.lock = false;
                            queue.items.shift();
                            this._checkAPIRequestQueue(queueKey);
                        })
                    }, 250)
                }
                else {
                    let _mustRetry = error.status == 429;
                    
                    if(_mustRetry) {
                        queue.retryCount++;
                        if(queue.retryCount > 15) {
                            _mustRetry = false;
                        }
                    }

                    if(_mustRetry) {
                        setTimeout(() => {
                            queue.lock = false;
                            this._checkAPIRequestQueue(queueKey);
                        }, 5000)
                    }
                    else {
                        queue.retryCount = 0;

                        let _delay = this._calculateAPIRequestNextDelay(_ctx.req, error, Date.now() - _timeStart);
                        this._onAPIRequestComplete(queueKey, this._onAPIRequestError(queueKey, _ctx, error), _delay);
                    }
                }
            })
        })
    }

    private _isAPIRequestSameContext(ctxa:ApiInterceptorQueueItem, ctxb:ApiInterceptorQueueItem) {
        if(ctxa == ctxb) return true;
        else {
            if(ctxa.req.method.toUpperCase() == 'GET' && ctxb.req.method.toUpperCase() == 'GET' && ctxa.req.urlWithParams == ctxb.req.urlWithParams) {
                return true
            }
        }
    }

    private _onRequestSuccess(queueKey: ApiInterceptorQueueKey, ctx:ApiInterceptorQueueItem, event:HttpEvent<any>) {
        const queue = this._queues[queueKey];
        let _retVal:Array<ApiInterceptorQueueItem> = [];

        for(let i = queue.items.length - 1; i >= 0; i--) {
            if(this._isAPIRequestSameContext(ctx, queue.items[i])) {
                _retVal.push(queue.items[i]);
                queue.items[i].observer.next(event);
            }
        }

        return _retVal;
    }

    private _onAPIRequestError(queueKey: ApiInterceptorQueueKey, ctx:ApiInterceptorQueueItem, error:any) {
        const queue = this._queues[queueKey];
        let _retVal:Array<ApiInterceptorQueueItem> = [];

        for(let i = queue.items.length - 1; i >= 0; i--) {
            if(this._isAPIRequestSameContext(ctx, queue.items[i])) {
                _retVal.push(queue.items[i]);
                queue.items[i].observer.error(error);
            }
        }

        return _retVal;
    }

    private _onAPIRequestComplete(queueKey: ApiInterceptorQueueKey, ctxList:Array<ApiInterceptorQueueItem>, delay:number) {
        const queue = this._queues[queueKey];
        queue.retryCount = 0;

        setTimeout(() => {
            for(let _ctx of ctxList) {
                _ctx.observer.complete()

                let _index = queue.items.indexOf(_ctx);
                if(_index != -1) {
                    queue.items.splice(_index, 1);
                }
            }
            queue.lock = false;
            this._checkAPIRequestQueue(queueKey);
        }, delay)
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if(this._isReleaseRequest(req)) {
            req = req.clone({
                setParams: { 't': Date.now().toString() }
            });
        }
        else if(this._isAssetsRequest(req)) {
            req = req.clone({
                setParams: { 'v': this.environmentService.getBuildTime() }
            });
        }
        else if(this._isEntouranceAPIRequest(req) || this._isTravelAPIRequest(req)) {
            req = req.clone({
                setHeaders: { 'PG-TransactionId': PGUtilities.getRandomId() }
            });
        }

        const queueKey = this._queueKeyForRequest(req);
        if(queueKey != null) {
            return new Observable<HttpEvent<any>> ((observer) => {
                this._queues[queueKey].items.push({ 
                    req: req, 
                    next: next, 
                    observer: observer 
                });
                this._checkAPIRequestQueue(queueKey);
            })
        }

        return next.handle(req);
    }
}
